import type {
  BudgetSignalRow,
  DecideBulkResultItem,
  PendingApprovalEntry,
  PendingApprovalsResult,
} from '@clockwise/clients/timesheet-approvals';
import { isoDateString } from '@clockwise/shared';
import {
  DateRangePopover,
  type DateRangePreset,
  type DateRangeValue,
  prettyDateRange,
  resolveDateRange,
} from '@clockwise/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import {
  Check,
  CheckCheck,
  ChevronLeft,
  ChevronRight,
  ChevronRight as ChevronRightSmall,
  CircleSlash,
  Loader2,
  MoreVertical,
  PartyPopper,
  Search as SearchIcon,
  TriangleAlert,
  X,
} from 'lucide-react';
import { useMemo, useState } from 'react';
import { toast } from 'sonner';
import { z } from 'zod';
import { decisionCopy, unlockedEntries } from '@/components/custom/approvals-reject';
import { ApprovedSearchDialog } from '@/components/custom/approved-search-dialog';
import {
  OverBudgetBadge,
  OverBudgetCell,
  type OverBudgetDetail,
} from '@/components/custom/budget-over-indicator';
import { EngagementLabel, engagementDisplay } from '@/components/custom/engagement-label';
import { EntityPicker } from '@/components/custom/entity-picker';
import { customerPickerConfig, userPickerConfig } from '@/components/custom/entity-pickers';
import { NoAccess } from '@/components/custom/no-access';
import {
  DisabledActionReason,
  LOCKED_ENTRY_APPROVE_TOOLTIP,
  LOCKED_ENTRY_REJECT_TOOLTIP,
  RejectReasonPopover,
} from '@/components/custom/reject-reason-popover';
import { BillableBadge } from '@/components/custom/timesheet-billable';
import {
  addDays,
  deltaTextClass,
  formatDelta,
  formatHours,
  mondayOf,
  shortDate,
  shortDayName,
  sundayOf,
  weekDates,
  weeklyFromMonthly,
} from '@/components/custom/timesheet-week';
import { UserRow } from '@/components/custom/user-row';
import { PageHeader } from '@/components/layout/page-header';
import { PageShell } from '@/components/layout/page-shell';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Skeleton } from '@/components/ui/skeleton';
import { timesheetApprovalsApi } from '@/lib/api';
import { dateRangePresetSchema } from '@/lib/date-range-search';
import { useMe } from '@/lib/me';
import { queryKeys } from '@/lib/query-keys';
import { cn, localTodayIso } from '@/lib/utils';

const VIEW_VALUES = ['employee', 'customer'] as const;
type ViewMode = (typeof VIEW_VALUES)[number];

// Group-atomic cap for date-range mode: return at most this many groups
// (employees or customers), each complete. Week mode is naturally bounded and
// omits the cap. Mirrors PENDING_GROUP_LIMIT on the API.
const GROUP_LIMIT = 50;

// Presets offered by the date-range view — the standard windows plus the
// opt-in "All time" (no bounds). Custom from/to is always available too.
const RANGE_PRESETS: readonly DateRangePreset[] = [
  'all_time',
  'current_week',
  'current_month',
  'previous_month',
  'current_quarter',
  'previous_quarter',
  'current_year',
  'previous_year',
  'custom',
];

const search = z.object({
  view: z.enum(VIEW_VALUES).optional(),
  // Period: undefined = date-range mode (the default); 'week' = week mode (the stepper).
  // `.catch(undefined)` keeps legacy links tolerant — before the default flip,
  // Date mode was reached via `?period=range`, so those bookmarked/shared URLs
  // (and the reminder email, though it links to a bare `/approvals`) resolve to
  // the now-default Date view instead of throwing at validateSearch.
  period: z.literal('week').optional().catch(undefined),
  week: isoDateString().optional(),
  // Date-range mode selection (preset + optional custom bounds).
  preset: dateRangePresetSchema.optional(),
  from: isoDateString().optional(),
  to: isoDateString().optional(),
  customerIds: z.array(z.string().uuid()).optional(),
  userIds: z.array(z.string().uuid()).optional(),
  // Testing-only escape hatch for the over-budget signal (docs/budgets.md §
  // Signals) — no permission gates it; appending this to the URL is the
  // whole mechanism. Absent (the default) ⇒ hidden for everyone. Remove
  // this param and always fetch/render once the signal is ready for
  // general availability.
  showOverBudget: z.coerce.boolean().optional(),
});
type Search = z.infer<typeof search>;

export const Route = createFileRoute('/_authenticated/approvals')({
  validateSearch: search,
  component: ApprovalsPage,
});

function ApprovalsPage() {
  const params = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const { loading: meLoading, permissions } = useMe();
  const queryClient = useQueryClient();

  // Direct-URL gate — sidebar already filters this item out, but a user
  // could land here via a deep link (e.g. the digest email) without the
  // permission, in which case we render the standard NoAccess card.
  // The check waits until permissions resolve to avoid a flash.
  const canApprove = permissions.has('timesheet.approve');

  const view: ViewMode = params.view ?? 'employee';
  const periodMode: 'week' | 'range' = params.period === 'week' ? 'week' : 'range';
  const weekStart = mondayOf(params.week ?? localTodayIso());
  const weekEnd = sundayOf(weekStart);

  // Date-range selection (range mode only). Default preset is "All time".
  const rangeValue: DateRangeValue = {
    preset: params.preset ?? 'all_time',
    from: params.from,
    to: params.to,
  };
  const resolvedRange = resolveDateRange(rangeValue);
  const isAllTime = rangeValue.preset === 'all_time';
  // A custom range is invalid until both bounds are set and ordered — gate the
  // fetch on it so we don't query (or show "nothing to approve") mid-edit.
  const rangeInvalid = periodMode === 'range' && !isAllTime && resolvedRange === null;

  // The concrete window sent to the API. Week mode → the Mon/Sun week; range
  // mode → the resolved preset/custom window, or no bounds for "All time".
  const windowStart =
    periodMode === 'week' ? weekStart : isAllTime ? undefined : resolvedRange?.from;
  const windowEnd = periodMode === 'week' ? weekEnd : isAllTime ? undefined : resolvedRange?.to;

  const customerIds = params.customerIds;
  const userIds = params.userIds;
  // Only range mode applies the group-atomic cap; week mode stays uncapped.
  const groupBy = periodMode === 'range' ? view : undefined;
  const groupLimit = periodMode === 'range' ? GROUP_LIMIT : undefined;

  const updateParams = (next: Partial<Search>) =>
    navigate({
      search: (prev: Search) => stripDefaults({ ...prev, ...next }),
      replace: true,
    });

  const pendingFilter = {
    weekStartDate: windowStart,
    weekEndDate: windowEnd,
    customerIds,
    userIds,
    groupBy,
    groupLimit,
  };

  const pendingQuery = useQuery({
    queryKey: queryKeys.timesheetApprovals.pending(pendingFilter),
    queryFn: () => timesheetApprovalsApi.pending(pendingFilter),
    enabled: canApprove && !rangeInvalid,
  });

  // The over-budget signal (docs/budgets.md § Signals) is still under test —
  // no permission gates it; `?showOverBudget=true` in the URL is the whole
  // mechanism, so it's invisible by default with no grant to manage. Same
  // filter shape as `pendingFilter`; the server ignores `groupBy`/
  // `groupLimit` since it re-derives the visible rows from
  // `findPendingForUser` itself.
  //
  // Remove this condition (always true) once the signal is ready for
  // general availability — it'll then be visible to every approver, same as
  // the rest of this page.
  const canViewBudgetSignal = params.showOverBudget === true;
  const budgetSignalsQuery = useQuery({
    queryKey: queryKeys.timesheetApprovals.budgetSignals(pendingFilter),
    queryFn: () => timesheetApprovalsApi.budgetSignals(pendingFilter),
    enabled: canApprove && canViewBudgetSignal && !rangeInvalid,
  });

  const countQuery = useQuery({
    queryKey: queryKeys.timesheetApprovals.count(),
    queryFn: () => timesheetApprovalsApi.count(),
    staleTime: 30_000,
    enabled: canApprove,
  });

  const pendingKey = [...queryKeys.timesheetApprovals.all, 'pending'] as const;

  /** One homogeneous decision batch — every UI action approves OR rejects
   *  a set of instances; rejects carry one shared reason for the scope.
   *  `skippedLocked` (approve only) is how many locked-period rows the
   *  handler filtered out of `instanceIds` before sending, so the result
   *  toast can report them as skipped rather than pretend they were done. */
  type DecisionBatch =
    | { decision: 'approved'; instanceIds: string[]; skippedLocked?: number }
    | { decision: 'rejected'; instanceIds: string[]; reason: string };

  const decideMutation = useMutation({
    // One mutation for both verbs: every click is a homogeneous batch, so
    // the verb rides alongside the instance ids and picks the endpoint.
    mutationFn: (batch: DecisionBatch) =>
      batch.decision === 'approved'
        ? timesheetApprovalsApi.approveBulk(batch.instanceIds)
        : timesheetApprovalsApi.rejectBulk(batch.instanceIds, batch.reason),
    // Optimistically drop the decided entries from every cached pending list
    // so a row vanishes the instant it's approved/rejected — before the
    // refetch lands. This closes the race where a reviewer rejects one day
    // and then bulk-approves the customer: without it, the stale list still
    // holds the rejected instance, the bulk approve re-sends it, and the
    // server returns a per-item ConflictError (surfaced as "1 failed"). The
    // server still guards the conflict either way; this just keeps the UI
    // honest and avoids the confusing toast. onSettled reconciles with the
    // authoritative server state.
    onMutate: async (batch) => {
      const ids = new Set(batch.instanceIds);
      await queryClient.cancelQueries({ queryKey: queryKeys.timesheetApprovals.all });
      const snapshot = queryClient.getQueriesData<{ data: PendingApprovalsResult }>({
        queryKey: pendingKey,
      });
      queryClient.setQueriesData<{ data: PendingApprovalsResult }>(
        { queryKey: pendingKey },
        (old) => {
          if (!old) return old;
          const kept = old.data.entries.filter((e) => !ids.has(e.approvalInstanceId));
          if (kept.length === old.data.entries.length) return old;
          return { ...old, data: { ...old.data, entries: kept, totals: pendingTotals(kept) } };
        },
      );
      return { snapshot };
    },
    onSuccess: (res, batch) => {
      reportDecideResults(
        res.data.results,
        batch.decision === 'approved' ? 'Approved' : 'Rejected',
        batch.decision === 'approved' ? (batch.skippedLocked ?? 0) : 0,
      );
    },
    onError: (err, _decisions, ctx) => {
      // Roll the optimistic removal back so failed entries reappear until
      // the onSettled refetch confirms the real state.
      if (ctx?.snapshot) {
        for (const [key, data] of ctx.snapshot) queryClient.setQueryData(key, data);
      }
      toast.error(err instanceof Error ? err.message : 'Failed to apply decisions');
    },
    onSettled: () => {
      setBusyKey(null);
      queryClient.invalidateQueries({ queryKey: queryKeys.timesheetApprovals.all });
    },
  });

  // Which specific button is mid-decision, so only that one shows a spinner
  // while `decideMutation.isPending` disables the rest. Buttons are disabled
  // during any in-flight decision, so there's never more than one at a time.
  const [busyKey, setBusyKey] = useState<string | null>(null);

  const entries = pendingQuery.data?.data.entries ?? [];
  const totals = pendingQuery.data?.data.totals ?? { entries: 0, hours: 0, amount: 0 };
  const groupTotal = pendingQuery.data?.data.groupTotal ?? 0;
  const returnedGroups = pendingQuery.data?.data.returnedGroups ?? 0;
  // Range mode capped the tail when more groups match than were returned.
  const truncated = periodMode === 'range' && groupTotal > returnedGroups;

  const groups = useMemo(
    () => buildGroups(entries, view, periodMode === 'week' ? weekDates(weekStart) : []),
    [entries, view, weekStart, periodMode],
  );

  // Per-entry overage, keyed by the SAME compound key `budgets[]` uses
  // (budgetId:roleId:periodStart) — lets `summarizeOverage` below sum the
  // AUTHORITATIVE period-level total for every distinct budget a group's
  // entries touch, rather than a possibly-partial sum of only the entries
  // visible on this page.
  const overByTimesheetId = useMemo(() => {
    const map = new Map<string, { overHours: number; budgetKey: string }>();
    for (const e of budgetSignalsQuery.data?.data.entries ?? []) {
      map.set(e.timesheetId, {
        overHours: e.overHours,
        budgetKey: `${e.budgetId}:${e.roleId}:${e.periodStart}`,
      });
    }
    return map;
  }, [budgetSignalsQuery.data]);

  const budgetByKey = useMemo(() => {
    const map = new Map<string, BudgetSignalRow>();
    for (const b of budgetSignalsQuery.data?.data.budgets ?? []) {
      map.set(`${b.budgetId}:${b.roleId}:${b.periodStart}`, b);
    }
    return map;
  }, [budgetSignalsQuery.data]);

  // Server-side "approve all" for range mode — clears the whole matching queue
  // (beyond the loaded cap), so it can't enumerate loaded ids like the week
  // path does. Fires the same toast/refetch handling via a shared helper.
  const approveAllMutation = useMutation({
    mutationFn: () =>
      timesheetApprovalsApi.approveAll({
        weekStartDate: windowStart,
        weekEndDate: windowEnd,
        customerIds,
        userIds,
      }),
    onSuccess: (res) => reportDecideResults(res.data.results, 'Approved'),
    onError: (err) => toast.error(err instanceof Error ? err.message : 'Failed to approve entries'),
    onSettled: () => {
      setBusyKey(null);
      queryClient.invalidateQueries({ queryKey: queryKeys.timesheetApprovals.all });
    },
  });

  const decideBusy = decideMutation.isPending || approveAllMutation.isPending;

  const [approveAllOpen, setApproveAllOpen] = useState(false);
  const [approvedSearchOpen, setApprovedSearchOpen] = useState(false);

  const approveAll = () => {
    setApproveAllOpen(false);
    if (periodMode === 'range') {
      // Uncapped server-side approve across the full filter. Locked-period
      // rows are vetoed per-item by the server guard; `reportDecideResults`
      // classifies those vetoes as skipped (not failed) in the toast.
      setBusyKey('all');
      approveAllMutation.mutate();
      return;
    }
    // Week mode enumerates loaded ids — skip locked rows the server would
    // veto anyway, so the toast reports only the entries actually approved.
    // Set the busy key only once we're actually mutating, or an early return
    // here would strand `busyKey='all'` (no mutation → no onSettled reset).
    const eligible = unlockedEntries(entries);
    if (eligible.length === 0) {
      if (entries.length > 0) allLockedToast('approved');
      return;
    }
    setBusyKey('all');
    decideMutation.mutate({
      decision: 'approved',
      instanceIds: eligible.map((e) => e.approvalInstanceId),
      skippedLocked: entries.length - eligible.length,
    });
  };

  // A group can vanish or turn fully locked between opening a confirm
  // popover and clicking confirm (another decision's onSettled refetch can
  // land in between). Never exit those paths silently — the popover has
  // already closed and discarded any typed reason, so without a toast the
  // click just does nothing.
  const staleSelectionToast = () =>
    toast.error(
      'These entries changed since the list loaded. Nothing was submitted — please retry.',
    );
  const allLockedToast = (verb: 'approved' | 'rejected') =>
    toast.error(
      `All of these entries are now in a locked timesheet period and can no longer be ${verb}.`,
    );

  const approveGroup = (groupKey: string) => {
    const group = groups.find((g) => g.key === groupKey);
    if (!group) {
      staleSelectionToast();
      return;
    }
    // Locked-period entries can no longer be approved (the server vetoes
    // them) — fan out only to the eligible rows, mirroring rejectGroup. The
    // trigger is disabled when the whole group is locked (mixed-group path).
    const eligible = unlockedEntries(group.entries);
    if (eligible.length === 0) {
      allLockedToast('approved');
      return;
    }
    setBusyKey(`approve:g:${groupKey}`);
    decideMutation.mutate({
      decision: 'approved',
      instanceIds: eligible.map((e) => e.approvalInstanceId),
      skippedLocked: group.entries.length - eligible.length,
    });
  };

  // Bulk-reject a customer or employee group with one shared reason.
  // The reason popover collects the text; the group handler fans it out
  // to every entry in the group. Per-entry reject is intentionally not
  // available — bulk-only keeps the page focused on whole-submission
  // review rather than line-item nitpicking.
  const rejectGroup = (groupKey: string, reason: string) => {
    const group = groups.find((g) => g.key === groupKey);
    if (!group) {
      staleSelectionToast();
      return;
    }
    // Locked-period entries can't be rejected (the server vetoes them) —
    // fan out only to the eligible rows. The trigger button is disabled
    // when the whole group is locked, so this is the mixed-group path.
    const eligible = unlockedEntries(group.entries);
    if (eligible.length === 0) {
      allLockedToast('rejected');
      return;
    }
    setBusyKey(`reject:g:${groupKey}`);
    decideMutation.mutate({
      decision: 'rejected',
      instanceIds: eligible.map((e) => e.approvalInstanceId),
      reason,
    });
  };

  // Second-level (sub-group) decisions — same fan-out as the group handlers,
  // keyed by the composite `${primaryId}:${secondaryId}`.
  const subGroupEntries = (subKey: string) =>
    groups.flatMap((g) => g.subGroups).find((s) => s.key === subKey)?.entries;

  const approveSubGroup = (subKey: string) => {
    const list = subGroupEntries(subKey);
    if (!list) {
      staleSelectionToast();
      return;
    }
    const eligible = unlockedEntries(list);
    if (eligible.length === 0) {
      allLockedToast('approved');
      return;
    }
    setBusyKey(`approve:sg:${subKey}`);
    decideMutation.mutate({
      decision: 'approved',
      instanceIds: eligible.map((e) => e.approvalInstanceId),
      skippedLocked: list.length - eligible.length,
    });
  };

  const rejectSubGroup = (subKey: string, reason: string) => {
    const list = subGroupEntries(subKey);
    if (!list) {
      staleSelectionToast();
      return;
    }
    const eligible = unlockedEntries(list);
    if (eligible.length === 0) {
      allLockedToast('rejected');
      return;
    }
    setBusyKey(`reject:sg:${subKey}`);
    decideMutation.mutate({
      decision: 'rejected',
      instanceIds: eligible.map((e) => e.approvalInstanceId),
      reason,
    });
  };

  // Per-entry decisions — the finest grain, sent as single-item batches so
  // the toast/partial-failure handling above covers them unchanged.
  const approveEntry = (instanceId: string) => {
    setBusyKey(`approve:e:${instanceId}`);
    decideMutation.mutate({ decision: 'approved', instanceIds: [instanceId] });
  };

  const rejectEntry = (instanceId: string, reason: string) => {
    setBusyKey(`reject:e:${instanceId}`);
    decideMutation.mutate({ decision: 'rejected', instanceIds: [instanceId], reason });
  };

  const counts = countQuery.data?.data ?? { thisWeek: 0, total: 0 };

  // Week mode loads every matching entry (uncapped), so we can count the
  // rows "Approve all" would actually touch — locked-period entries are
  // vetoed by the server guard and skipped by `approveAll`, so exclude them.
  // Drives the confirm-popover count and the button's disabled state (the
  // button label itself carries no count). Range mode caps loaded entries,
  // so this value is unused there — skip the filter.
  const weekApprovableCount = useMemo(
    () => (periodMode === 'week' ? unlockedEntries(entries).length : 0),
    [entries, periodMode],
  );

  if (!meLoading && !canApprove) {
    return <NoAccess permission="timesheet.approve" resource="Approvals" />;
  }

  return (
    <PageShell>
      <PageHeader
        title="Approvals"
        subtitle={
          <>
            {counts.total} entr{counts.total === 1 ? 'y' : 'ies'} awaiting approval across all weeks
            {/* Week mode adds the current-week count; a bounded range adds its
                (loaded) count. All-time range needs no second number — it IS
                "across all weeks", and totals.entries is group-capped so it
                would contradict the uncapped total on the same line. */}
            {periodMode === 'week'
              ? ` · ${counts.thisWeek} in this week`
              : isAllTime
                ? ''
                : ` · ${totals.entries} in this range`}
          </>
        }
      >
        <Popover open={approveAllOpen} onOpenChange={setApproveAllOpen}>
          <PopoverTrigger asChild>
            {/* `PopoverTrigger asChild` clobbers this Button's own
                `data-slot="button"` with its own `data-slot="popover-trigger"`
                (Radix Slot lets the outer wrapper's explicit props win), so the
                global `[data-slot='button']` pill-radius rule never matches this
                trigger. Set the radius directly instead of depending on that rule. */}
            <Button
              size="sm"
              className="rounded-full"
              disabled={
                (periodMode === 'week' ? weekApprovableCount === 0 : entries.length === 0) ||
                decideBusy
              }
            >
              <CheckCheck />
              {/* No count on the button itself: range mode can only show the
                    uncapped whole-queue total (locked rows included), which
                    would overstate what actually gets approved. The confirm
                    popover carries the exact week-mode count instead. */}
              Approve all
            </Button>
          </PopoverTrigger>
          <PopoverContent align="end" className="w-96">
            <div className="space-y-3">
              <div className="space-y-1">
                <h4 className="text-sm font-semibold">
                  {periodMode === 'week'
                    ? `Approve ${weekApprovableCount} entr${weekApprovableCount === 1 ? 'y' : 'ies'}?`
                    : 'Approve every matching entry?'}
                </h4>
                <p className="text-xs text-muted-foreground">
                  {periodMode === 'week'
                    ? 'This approves every unlocked entry across the current filters; any locked-period entries are skipped — please request support to unlock the period first.'
                    : truncated
                      ? `This approves every pending entry matching the current filters — all ${groupTotal} ${view === 'employee' ? 'employees' : 'customers'} in the selected range, including the ${groupTotal - returnedGroups} not shown below.`
                      : 'This approves every pending entry matching the current filters and date range.'}
                </p>
              </div>
              <div className="flex items-center justify-end gap-2">
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => setApproveAllOpen(false)}
                  disabled={decideBusy}
                >
                  Cancel
                </Button>
                <Button size="sm" onClick={approveAll} disabled={decideBusy}>
                  {busyKey === 'all' ? <Loader2 className="animate-spin" /> : null}
                  {decideBusy ? 'Approving…' : 'Approve'}
                </Button>
              </div>
            </div>
          </PopoverContent>
        </Popover>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            {/* Same `data-slot` clobbering as the Approve all trigger above —
                set the pill radius directly. */}
            <Button
              size="icon-sm"
              variant="ghost"
              className="rounded-full border border-border"
              aria-label="More actions"
            >
              <MoreVertical />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            {/* The only item in this menu, so it reads as a standalone action
                rather than a row in a list — a bordered pill/chip instead of
                the plain flush-hover row every other DropdownMenuItem in the
                app uses. Scoped to this one instance via className, not the
                shared `dropdown-menu.tsx` (G9) or a global rule. */}
            <DropdownMenuItem
              onSelect={() => setApprovedSearchOpen(true)}
              className="rounded-full border border-border bg-muted"
            >
              <SearchIcon />
              Search Approved
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </PageHeader>

      <div
        data-surface="panel"
        className="flex flex-wrap items-center gap-3 rounded-md border bg-card p-6 py-4"
      >
        {/* Grouping dimension first — it frames how the rest of the bar reads. */}
        <div
          data-surface="panel"
          data-slot="toggle-group"
          className="inline-flex items-center gap-0 rounded-md border light:bg-card p-0.5"
        >
          <ViewButton
            active={view === 'employee'}
            onClick={() => updateParams({ view: undefined })}
          >
            By employee
          </ViewButton>
          <ViewButton
            active={view === 'customer'}
            onClick={() => updateParams({ view: 'customer' })}
          >
            By customer
          </ViewButton>
        </div>

        {/* Period mode: step through one week at a time, or scope to an
            arbitrary date range (incl. all time). The temporal control to its
            right swaps with the mode. */}
        <div
          data-surface="panel"
          data-slot="toggle-group"
          className="inline-flex items-center gap-0 rounded-md border light:bg-card p-0.5"
        >
          <ViewButton
            active={periodMode === 'range'}
            onClick={() => updateParams({ period: undefined, week: undefined })}
          >
            Date
          </ViewButton>
          <ViewButton
            active={periodMode === 'week'}
            onClick={() =>
              updateParams({ period: 'week', preset: undefined, from: undefined, to: undefined })
            }
          >
            Week
          </ViewButton>
        </div>

        {periodMode === 'week' ? (
          <div className="flex items-center gap-1">
            <Button
              size="icon-sm"
              variant="ghost"
              aria-label="Previous week"
              onClick={() => updateParams({ week: addDays(weekStart, -7) })}
            >
              <ChevronLeft />
            </Button>
            <span className="text-sm font-medium tabular-nums">
              {shortDate(weekStart, { withYear: true })} – {shortDate(weekEnd, { withYear: true })}
            </span>
            <Button
              size="icon-sm"
              variant="ghost"
              aria-label="Next week"
              onClick={() => updateParams({ week: addDays(weekStart, 7) })}
            >
              <ChevronRight />
            </Button>
          </div>
        ) : (
          // Compact popover: preset sidebar + two-month range calendar (CRM
          // pattern), over clockwise's presets — no sprawl across the toolbar.
          <DateRangePopover
            id="approvals-range"
            presets={RANGE_PRESETS}
            value={rangeValue}
            onChange={(next) =>
              updateParams({
                preset: next.preset,
                from: next.preset === 'custom' ? next.from : undefined,
                to: next.preset === 'custom' ? next.to : undefined,
              })
            }
          />
        )}

        {/* Multi-select pickers (shared app-wide combobox). `lazy` sends the
            typed term to the server (debounced `q=`) so tenants with more than
            one page of customers/employees can still find rows by name. Empty
            selection = "all". Ids are sorted before they hit the URL so pick
            order doesn't fork the query cache. */}
        <div className="min-w-52 max-w-md">
          <EntityPicker
            multiple
            id="approvals-customer-filter"
            config={customerPickerConfig}
            value={customerIds ?? []}
            onChange={(ids) =>
              updateParams({ customerIds: ids.length ? [...ids].sort() : undefined })
            }
            placeholder="All customers"
            searchPlaceholder="Search customers…"
            loadStrategy="lazy"
            popoverWidth="auto"
            badgeOverflow="scroll"
            className="min-h-8"
          />
        </div>
        <div className="min-w-52 max-w-md">
          <EntityPicker
            multiple
            id="approvals-employee-filter"
            config={userPickerConfig}
            value={userIds ?? []}
            onChange={(ids) => updateParams({ userIds: ids.length ? [...ids].sort() : undefined })}
            placeholder="All employees"
            searchPlaceholder="Search employees…"
            loadStrategy="lazy"
            popoverWidth="auto"
            badgeOverflow="scroll"
            renderItem={(u, isSelected) => <UserRow user={u} isSelected={isSelected} />}
            className="min-h-8"
          />
        </div>

        {customerIds?.length || userIds?.length ? (
          <Button
            variant="ghost"
            size="sm"
            className="h-8 gap-1 text-muted-foreground"
            onClick={() => updateParams({ customerIds: undefined, userIds: undefined })}
          >
            <X className="size-3.5" />
            Clear
          </Button>
        ) : null}
      </div>

      {rangeInvalid ? (
        <div className="flex flex-col items-center justify-center rounded-md border border-dashed bg-card/40 p-12 text-center">
          <CircleSlash className="mb-3 size-8 text-muted-foreground" />
          <h2 className="text-sm font-semibold">Pick a date range</h2>
          <p className="mt-1 max-w-md text-xs text-muted-foreground">
            Choose a preset or set a valid From/To (To on or after From) to load pending entries.
          </p>
        </div>
      ) : pendingQuery.isLoading || meLoading ? (
        <div className="space-y-2 p-2">
          <Skeleton className="h-12 w-full" />
          <Skeleton className="h-12 w-full" />
          <Skeleton className="h-12 w-full" />
        </div>
      ) : entries.length === 0 ? (
        <EmptyState
          weekLabel={
            periodMode === 'week'
              ? `${shortDate(weekStart)} – ${shortDate(weekEnd)}`
              : isAllTime
                ? 'the selected filters'
                : prettyDateRange(resolvedRange)
          }
          hasFilters={Boolean(customerIds?.length || userIds?.length)}
          onClearFilters={() => updateParams({ customerIds: undefined, userIds: undefined })}
        />
      ) : (
        <ApprovalsTable
          weekStart={weekStart}
          view={view}
          mode={periodMode}
          groups={groups}
          totals={totals}
          truncated={truncated}
          groupTotal={groupTotal}
          returnedGroups={returnedGroups}
          decideLoading={decideBusy}
          busyKey={busyKey}
          onApproveGroup={approveGroup}
          onRejectGroup={rejectGroup}
          onApproveSubGroup={approveSubGroup}
          onRejectSubGroup={rejectSubGroup}
          onApproveEntry={approveEntry}
          onRejectEntry={rejectEntry}
          canViewBudgetSignal={canViewBudgetSignal}
          overByTimesheetId={overByTimesheetId}
          budgetByKey={budgetByKey}
        />
      )}
      <ApprovedSearchDialog open={approvedSearchOpen} onOpenChange={setApprovedSearchOpen} />
    </PageShell>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Grouping
// ─────────────────────────────────────────────────────────────────────────

/** Shared hour/amount totals + 7-day matrix used by both grouping levels. */
interface GroupRollup {
  totalHours: number;
  totalAmount: number;
  /** Per-day breakdown for the matrix view (7 cells). */
  perDay: Array<{ date: string; hours: number; amount: number; count: number }>;
}

function rollupEntries(list: PendingApprovalEntry[], dates: string[]): GroupRollup {
  let totalHours = 0;
  let totalAmount = 0;
  const perDayMap = new Map<string, { hours: number; amount: number; count: number }>();
  for (const d of dates) perDayMap.set(d, { hours: 0, amount: 0, count: 0 });
  for (const entry of list) {
    totalHours += entry.hours;
    if (entry.amount != null) totalAmount += entry.amount;
    const cell = perDayMap.get(entry.date);
    if (cell) {
      cell.hours += entry.hours;
      if (entry.amount != null) cell.amount += entry.amount;
      cell.count += 1;
    }
  }
  return {
    totalHours: round2(totalHours),
    totalAmount: round2(totalAmount),
    perDay: dates.map((d) => {
      const cell = perDayMap.get(d)!;
      return { date: d, hours: round2(cell.hours), amount: round2(cell.amount), count: cell.count };
    }),
  };
}

/** Second-level rollup: the customer (employee view) or employee (customer view). */
interface ApprovalsSubGroup extends GroupRollup {
  /** `${primaryId}:${secondaryId}` — unique across the page, never collides with an L1 key. */
  key: string;
  id: string;
  title: string;
  initials: string;
  entries: PendingApprovalEntry[];
}

interface ApprovalsGroup extends GroupRollup {
  key: string;
  /** Identifier of the grouping (employee userId or customer customerId). */
  id: string;
  /** Display title — employee name or customer name. */
  title: string;
  /** Sub-line under the title. Employee: role; customer: total hours/wk. */
  subtitle: string | null;
  /** Avatar initials. */
  initials: string;
  /** Target hours/week for employee view; null for customer view. */
  targetHoursPerWeek: number | null;
  entries: PendingApprovalEntry[];
  /** Second-level rollups, sorted alphabetically by title. */
  subGroups: ApprovalsSubGroup[];
}

function buildGroups(
  entries: PendingApprovalEntry[],
  view: ViewMode,
  // Week mode passes the 7 weekday ISO dates for the per-day matrix; range mode
  // passes [] (no fixed day grid) — an empty `dates` yields no per-day cells
  // while the rollup totals still sum over every entry.
  dates: string[],
): ApprovalsGroup[] {
  if (entries.length === 0) return [];
  const primaryKeyFn = (e: PendingApprovalEntry) => (view === 'employee' ? e.userId : e.customerId);
  const secondaryKeyFn = (e: PendingApprovalEntry) =>
    view === 'employee' ? e.customerId : e.userId;

  const byPrimary = new Map<string, PendingApprovalEntry[]>();
  for (const entry of entries) {
    const key = primaryKeyFn(entry);
    const list = byPrimary.get(key);
    if (list) list.push(entry);
    else byPrimary.set(key, [entry]);
  }

  const groups: ApprovalsGroup[] = [];
  for (const [primaryId, list] of byPrimary) {
    const first = list[0]!;

    // Second-level (sub-group) breakdown: customer (employee view) or employee
    // (customer view). Week mode renders these as matrix rows, range mode as
    // matrix-free rows — same data either way (empty `dates` just drops perDay).
    const bySecondary = new Map<string, PendingApprovalEntry[]>();
    for (const entry of list) {
      const key = secondaryKeyFn(entry);
      const sub = bySecondary.get(key);
      if (sub) sub.push(entry);
      else bySecondary.set(key, [entry]);
    }
    const subGroups: ApprovalsSubGroup[] = [];
    for (const [secondaryId, subList] of bySecondary) {
      const sf = subList[0]!;
      subGroups.push({
        key: `${primaryId}:${secondaryId}`,
        id: secondaryId,
        title:
          view === 'employee'
            ? sf.customerName
            : `${sf.userFirstName} ${sf.userLastName}`.trim() || sf.userEmail,
        initials:
          view === 'employee'
            ? customerInitials(sf.customerName)
            : initialsFor(sf.userFirstName, sf.userLastName, sf.userEmail),
        entries: subList,
        ...rollupEntries(subList, dates),
      });
    }
    subGroups.sort((a, b) => a.title.localeCompare(b.title));

    const rollup = rollupEntries(list, dates);
    groups.push(
      view === 'employee'
        ? {
            key: primaryId,
            id: first.userId,
            title: `${first.userFirstName} ${first.userLastName}`.trim() || first.userEmail,
            subtitle: first.roleName,
            initials: initialsFor(first.userFirstName, first.userLastName, first.userEmail),
            // Convert monthly capacity → weekly target at the boundary
            // so downstream delta math stays in weekly units.
            targetHoursPerWeek: weeklyFromMonthly(first.userHoursPerMonth),
            entries: list,
            subGroups,
            ...rollup,
          }
        : {
            key: primaryId,
            id: first.customerId,
            title: first.customerName,
            subtitle: null,
            initials: customerInitials(first.customerName),
            targetHoursPerWeek: null,
            entries: list,
            subGroups,
            ...rollup,
          },
    );
  }

  // Sort: alphabetical by title (so the page is deterministic across renders).
  groups.sort((a, b) => a.title.localeCompare(b.title));
  return groups;
}

// ─────────────────────────────────────────────────────────────────────────
// Table
// ─────────────────────────────────────────────────────────────────────────

// Shared column template for the L1 group header, L2 sub-group header, and
// the daily-totals footer — keeps the 7-day matrix columns aligned across
// all three levels. Each row is its own grid, so every track must be
// content-independent or the rows won't line up. The trailing actions track
// is a FIXED width (not `auto`): data rows render the Approve/Reject buttons
// there while the header/footer render short text, and an `auto` track would
// size to that content — stealing space from the `1fr` day columns and
// shifting the per-day hours left in data rows only.
//
// Two templates, switched by a CONTAINER query on the scroll card
// (`@container/approvals`, not the viewport — what matters is the card's own
// width). Roomy (`@6xl` ≈ container ≥1152px): comfortable columns + a 200px
// actions track that fits the labelled "Approve"/"Reject" buttons. Constrained
// (below that): tighter matrix mins and an 88px actions track — the buttons
// drop their labels to icon-only (see the `@6xl/approvals:inline` label spans
// on each button), so the per-day hours never get clipped on a small laptop.
// The `minmax(px,…fr)` mins are the horizontal-scroll floor; below the
// constrained floor (~820px) the card scrolls (a 7-day matrix can't compress
// further). The name wraps to multiple lines and day cells hold short hours
// text, so the tight mins stay readable.
const TABLE_GRID =
  'grid-cols-[minmax(220px,2fr)_repeat(7,minmax(52px,1fr))_minmax(96px,1fr)_88px] @6xl/approvals:grid-cols-[minmax(280px,2fr)_repeat(7,minmax(64px,1fr))_minmax(140px,1fr)_200px]';

// L3 detail rows have their own template (indented, no matrix). The trailing
// track holds the per-entry Approve/Reject buttons — fixed width so it doesn't
// steal space from the description as button labels render in data rows only.
// Same container-query split as TABLE_GRID for the actions track (88px icon /
// 200px labelled).
const DETAIL_GRID =
  'grid-cols-[96px_minmax(150px,1.3fr)_96px_minmax(180px,2fr)_110px_88px] @6xl/approvals:grid-cols-[96px_minmax(150px,1.3fr)_96px_minmax(180px,2fr)_110px_200px]';

// Approve/Reject button text — hidden when the approvals card is space-
// constrained (icon-only mode), shown once the container reaches @6xl, in
// lockstep with the 88px→200px actions track in TABLE_GRID / DETAIL_GRID. The
// button's `title` keeps the action labelled for hover + assistive tech even
// when the text is hidden.
function ActionLabel({ children }: { children: React.ReactNode }) {
  return <span className="hidden @6xl/approvals:inline">{children}</span>;
}

// Swaps an Approve/Reject button's leading icon for a spinner while that exact
// action is in flight. The icon-only (space-constrained) layout still gets
// feedback because the spinner replaces the icon in place.
function ActionIcon({ busy, icon: Icon }: { busy: boolean; icon: typeof Check }) {
  return busy ? <Loader2 className="size-3.5 animate-spin" /> : <Icon className="size-3.5" />;
}

// ─────────────────────────────────────────────────────────────────────────
// Over-budget signal (docs/budgets.md § Signals)
// ─────────────────────────────────────────────────────────────────────────

/** Per-entry overage, keyed by `timesheetId`. `budgetKey` is
 *  `budgetId:roleId:periodStart` — the same compound key `budgetByKey` uses,
 *  so a group/day can collect the DISTINCT budgets its entries touch without
 *  re-deriving the key shape. */
type OverByTimesheetId = ReadonlyMap<string, { overHours: number; budgetKey: string }>;

/**
 * Aggregate over-budget summary for a set of entries (an employee/customer
 * group, or a sub-group): the distinct budgets their over-flagged entries
 * touch, the AUTHORITATIVE period-level overage for each (not a possibly-
 * partial sum of only these entries), and the tooltip breakdown. `null` when
 * none of the entries carry a signal.
 */
function summarizeOverage(
  entries: readonly PendingApprovalEntry[],
  overByTimesheetId: OverByTimesheetId,
  budgetByKey: ReadonlyMap<string, BudgetSignalRow>,
): { totalOverHours: number; budgetCount: number; details: OverBudgetDetail[] } | null {
  const keys = new Set<string>();
  for (const e of entries) {
    const over = overByTimesheetId.get(e.timesheetId);
    if (over) keys.add(over.budgetKey);
  }
  if (keys.size === 0) return null;

  let totalOverHours = 0;
  const budgetIds = new Set<string>();
  const details: OverBudgetDetail[] = [];
  for (const key of keys) {
    const row = budgetByKey.get(key);
    if (!row) continue;
    totalOverHours += row.overHours;
    budgetIds.add(row.budgetId);
    details.push({
      roleName: row.roleName,
      periodLabel: row.periodLabel,
      budgetHours: row.budgetHours,
      consumedHours: row.consumedHours,
    });
  }
  if (budgetIds.size === 0) return null;
  return { totalOverHours: round2(totalOverHours), budgetCount: budgetIds.size, details };
}

/** Sum of over-flagged hours for entries in `entries` that fall on `date`. */
function dayOverHours(
  entries: readonly PendingApprovalEntry[],
  date: string,
  overByTimesheetId: OverByTimesheetId,
): number {
  let sum = 0;
  for (const e of entries) {
    if (e.date !== date) continue;
    const over = overByTimesheetId.get(e.timesheetId);
    if (over) sum += over.overHours;
  }
  return round2(sum);
}

interface ApprovalsTableProps {
  weekStart: string;
  view: ViewMode;
  mode: 'week' | 'range';
  groups: ApprovalsGroup[];
  totals: { entries: number; hours: number; amount: number };
  /** Range mode only: the cap trimmed the group tail. */
  truncated: boolean;
  groupTotal: number;
  returnedGroups: number;
  decideLoading: boolean;
  /** Key of the single in-flight action, or null — drives per-button spinners. */
  busyKey: string | null;
  onApproveGroup: (key: string) => void;
  onRejectGroup: (key: string, reason: string) => void;
  onApproveSubGroup: (key: string) => void;
  onRejectSubGroup: (key: string, reason: string) => void;
  onApproveEntry: (instanceId: string) => void;
  onRejectEntry: (instanceId: string, reason: string) => void;
  /** Whether the caller may see the over-budget signal at all — driven by
   *  `?showOverBudget=true` in the URL, not a permission. False renders
   *  none of it. */
  canViewBudgetSignal: boolean;
  /** Per-entry overage, keyed by `timesheetId`. See `OverEntry`/`budgetByKey`. */
  overByTimesheetId: OverByTimesheetId;
  /** The signal's period-level totals, keyed by `budgetId:roleId:periodStart`
   *  — the same compound key `overByTimesheetId`'s `budgetKey` uses. */
  budgetByKey: ReadonlyMap<string, BudgetSignalRow>;
}

function ApprovalsTable(props: ApprovalsTableProps) {
  // Range mode can span weeks, so it drops the 7-day matrix for a flat,
  // group-atomic list. Week mode keeps the matrix rendering below.
  if (props.mode === 'range') return <ApprovalsFlatList {...props} />;
  return <ApprovalsWeekTable {...props} />;
}

// Shared expand/collapse state for the two-level group tables (week matrix and
// range flat list). Tracks a Set of open keys: L1 group keys plus
// `${groupKey}:${subKey}` sub-group keys.
function useExpandableGroups(groups: ApprovalsGroup[]) {
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  // Generic single-key toggle. Collapsing an L1 group also drops its sub-group
  // keys (`${key}:…`) so they don't reappear pre-expanded when it re-opens.
  const toggle = (key: string) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
        for (const k of next) if (k.startsWith(`${key}:`)) next.delete(k);
      } else {
        next.add(key);
      }
      return next;
    });

  // Toggle an L1 group; opening one that has a single sub-group auto-opens that
  // sub-group, so a one-customer employee (or one-employee customer) reveals its
  // entries in one click instead of two — the lone sub-group row is otherwise a
  // redundant tier repeating the group's own count. Still collapsible on its own.
  const toggleGroup = (g: ApprovalsGroup) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(g.key)) {
        next.delete(g.key);
        for (const k of next) if (k.startsWith(`${g.key}:`)) next.delete(k);
      } else {
        next.add(g.key);
        if (g.subGroups.length === 1) next.add(g.subGroups[0]!.key);
      }
      return next;
    });

  // "Expand all" opens both levels so every detail entry is visible without
  // per-row clicking. `allExpanded` is true only when every group AND sub-group
  // key is present, so the header chevron flips to "collapse all" exactly when
  // there's nothing left to open.
  const allExpanded =
    groups.length > 0 &&
    groups.every((g) => expanded.has(g.key) && g.subGroups.every((sg) => expanded.has(sg.key)));
  const toggleAll = () =>
    setExpanded(() => {
      if (allExpanded) return new Set();
      const next = new Set<string>();
      for (const g of groups) {
        next.add(g.key);
        for (const sg of g.subGroups) next.add(sg.key);
      }
      return next;
    });

  return { expanded, toggle, toggleGroup, allExpanded, toggleAll };
}

function ApprovalsWeekTable({
  weekStart,
  view,
  groups,
  totals,
  decideLoading,
  busyKey,
  onApproveGroup,
  onRejectGroup,
  onApproveSubGroup,
  onRejectSubGroup,
  onApproveEntry,
  onRejectEntry,
  canViewBudgetSignal,
  overByTimesheetId,
  budgetByKey,
}: ApprovalsTableProps) {
  const dates = weekDates(weekStart);
  // Week rows toggle straight to their matrix sub-groups (no single-sub-group
  // auto-open — the matrix itself is the payload here), so this uses `toggle`.
  const { expanded, toggle, allExpanded, toggleAll } = useExpandableGroups(groups);

  return (
    // The card is itself the scroll region (mirrors StickyTableScroll on the list
    // pages): `overflow-auto` makes it the single scrollport and clips its rounded
    // corners, so the sticky header below pins flush to the top of this box. The
    // page (PageShell) deliberately does NOT scroll — otherwise rows would bleed
    // through PageShell's top padding in the strip above the pinned header.
    <div className="@container/approvals min-h-0 flex-1 overflow-auto rounded-md border bg-card">
      <div
        className={cn(
          // `sticky top-0` pins the column header to the top while the rows (and
          // any expanded sub-rows) scroll under it. `bg-muted` is opaque on
          // purpose — a translucent `bg-muted/40` would let rows bleed through
          // once the header is pinned.
          'sticky top-0 z-10 grid items-center gap-2 border-b bg-muted px-4 py-3 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground',
          TABLE_GRID,
        )}
      >
        <button
          type="button"
          onClick={toggleAll}
          aria-expanded={allExpanded}
          disabled={groups.length === 0}
          title={allExpanded ? 'Collapse all rows' : 'Expand all rows'}
          className="flex items-center gap-1.5 text-left uppercase tracking-wider text-muted-foreground hover:text-foreground disabled:pointer-events-none disabled:opacity-50"
        >
          <ChevronRightSmall
            className={cn('size-3.5 shrink-0 transition-transform', allExpanded && 'rotate-90')}
          />
          {view === 'employee' ? 'Employee' : 'Customer'}
        </button>
        {dates.map((d) => (
          <div key={d} className="px-1.5 text-right">
            <div>{shortDayName(d)}</div>
            <div className="text-foreground/70 tabular-nums">{shortDate(d).split(' ')[1]}</div>
          </div>
        ))}
        <div className="text-right">Total{view === 'employee' ? ' · vs target' : ''}</div>
        <div className="text-right">Actions</div>
      </div>

      {groups.map((g) => {
        const isOpen = expanded.has(g.key);
        const delta = g.targetHoursPerWeek != null ? g.totalHours - g.targetHoursPerWeek : null;
        const overage = canViewBudgetSignal
          ? summarizeOverage(g.entries, overByTimesheetId, budgetByKey)
          : null;
        return (
          <div key={g.key} className="border-b last:border-b-0">
            {/* biome-ignore lint/a11y/useSemanticElements: row contains nested Approve/Reject buttons, so a native <button> would be invalid HTML; role="button" + keyboard handler keeps it accessible */}
            <div
              role="button"
              tabIndex={0}
              aria-expanded={isOpen}
              onClick={() => toggle(g.key)}
              onKeyDown={(e) => {
                // Only handle keys aimed at the row itself. React routes
                // synthetic events through the component tree, so keystrokes
                // from the portalled Reject popover's textarea (and the
                // nested Approve/Reject buttons) bubble up here — without this
                // guard, typing a space would be swallowed and toggle the row.
                if (e.target !== e.currentTarget) return;
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  toggle(g.key);
                }
              }}
              className={cn(
                'grid w-full cursor-pointer items-center gap-2 px-3 py-3 text-left hover:bg-muted/30',
                TABLE_GRID,
              )}
            >
              <div className="flex min-w-0 items-center gap-2">
                <ChevronRightSmall
                  className={cn(
                    'size-3.5 shrink-0 text-muted-foreground transition-transform',
                    isOpen && 'rotate-90',
                  )}
                />
                <span
                  className={cn(
                    'inline-flex size-7 shrink-0 items-center justify-center rounded-full text-[11px] font-semibold',
                    view === 'employee'
                      ? 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900/60 dark:text-indigo-200'
                      : 'bg-amber-100 text-amber-800 dark:bg-amber-900/60 dark:text-amber-200',
                  )}
                  aria-hidden
                >
                  {g.initials}
                </span>
                <div className="flex min-w-0 flex-col">
                  <span className="break-words text-sm font-semibold">{g.title}</span>
                  {g.subtitle ? (
                    <span className="truncate text-xs text-muted-foreground">
                      {g.subtitle}
                      {g.targetHoursPerWeek != null
                        ? ` · ${Math.round(g.targetHoursPerWeek)}h/wk`
                        : ''}
                      <span className="ml-1.5 text-[11px] font-medium">{g.entries.length}</span>
                    </span>
                  ) : (
                    <span className="text-xs text-muted-foreground">
                      <span className="text-[11px] font-medium">{g.entries.length}</span>{' '}
                      {g.entries.length === 1 ? 'entry' : 'entries'}
                    </span>
                  )}
                  {overage ? (
                    <OverBudgetBadge
                      overHours={overage.totalOverHours}
                      budgetCount={overage.budgetCount}
                      details={overage.details}
                      className="mt-1 w-fit"
                    />
                  ) : null}
                </div>
              </div>
              {g.perDay.map((cell) => {
                const dayOver = canViewBudgetSignal
                  ? dayOverHours(g.entries, cell.date, overByTimesheetId)
                  : 0;
                return (
                  <div
                    key={cell.date}
                    className={cn(
                      'rounded-md px-1.5 py-1 text-right text-sm tabular-nums',
                      dayOver > 0
                        ? 'bg-red-50 dark:bg-red-950/30'
                        : cell.count > 0
                          ? 'bg-amber-50/60 dark:bg-amber-950/30'
                          : 'text-muted-foreground',
                    )}
                  >
                    <div className="font-medium">
                      {cell.count > 0 ? formatHours(cell.hours) : '—'}
                    </div>
                    {dayOver > 0 ? (
                      <div className="text-[10px] font-semibold text-red-700 dark:text-red-300">
                        {formatHours(dayOver)} over
                      </div>
                    ) : null}
                  </div>
                );
              })}
              <div className="text-right text-sm tabular-nums">
                <div className="font-semibold">{formatHours(g.totalHours)}</div>
                {g.targetHoursPerWeek != null && delta != null ? (
                  <div className={cn('text-[11px]', deltaTextClass(delta))}>
                    {formatDelta(delta)}
                  </div>
                ) : null}
              </div>
              <div className="flex items-center justify-end gap-1">
                <GroupApproveButton
                  entries={g.entries}
                  scopeLabel={g.title}
                  busy={busyKey === `approve:g:${g.key}`}
                  loading={decideLoading}
                  onApprove={() => onApproveGroup(g.key)}
                />
                <GroupRejectButton
                  entries={g.entries}
                  scopeLabel={g.title}
                  busy={busyKey === `reject:g:${g.key}`}
                  loading={decideLoading}
                  onConfirm={(reason) => onRejectGroup(g.key, reason)}
                />
              </div>
            </div>

            {isOpen ? (
              <div className="bg-muted/10">
                {g.subGroups.map((sg) => (
                  <SubGroupSection
                    key={sg.key}
                    subGroup={sg}
                    view={view}
                    mode="week"
                    expanded={expanded.has(sg.key)}
                    onToggle={() => toggle(sg.key)}
                    decideLoading={decideLoading}
                    busyKey={busyKey}
                    onApprove={onApproveSubGroup}
                    onReject={onRejectSubGroup}
                    onApproveEntry={onApproveEntry}
                    onRejectEntry={onRejectEntry}
                    canViewBudgetSignal={canViewBudgetSignal}
                    overByTimesheetId={overByTimesheetId}
                    budgetByKey={budgetByKey}
                  />
                ))}
              </div>
            ) : null}
          </div>
        );
      })}

      <div
        className={cn(
          'grid items-center gap-2 bg-muted/60 px-3 py-2 text-xs font-medium',
          TABLE_GRID,
        )}
      >
        <div className="text-muted-foreground">Daily totals</div>
        {weekDates(weekStart).map((d) => {
          const sum = groups.reduce<{ hours: number; amount: number }>(
            (acc, g) => {
              const cell = g.perDay.find((c) => c.date === d);
              if (cell) {
                acc.hours += cell.hours;
                acc.amount += cell.amount;
              }
              return acc;
            },
            { hours: 0, amount: 0 },
          );
          return (
            <div key={d} className="px-1.5 text-right tabular-nums">
              <div className="font-semibold">{sum.hours > 0 ? formatHours(sum.hours) : '—'}</div>
            </div>
          );
        })}
        <div className="text-right tabular-nums">
          <div className="font-semibold">{formatHours(totals.hours)}</div>
        </div>
        <div />
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Flat list (date-range mode)
// ─────────────────────────────────────────────────────────────────────────

// No 7-day matrix — a range can span weeks. Just name · total · actions, and
// each group expands straight to its dated entry rows (reusing GroupEntries).
const FLAT_GRID =
  'grid-cols-[minmax(200px,1fr)_minmax(84px,120px)_88px] @6xl/approvals:grid-cols-[minmax(280px,1fr)_minmax(120px,160px)_200px]';

function ApprovalsFlatList({
  view,
  groups,
  truncated,
  groupTotal,
  returnedGroups,
  decideLoading,
  busyKey,
  onApproveGroup,
  onRejectGroup,
  onApproveSubGroup,
  onRejectSubGroup,
  onApproveEntry,
  onRejectEntry,
  canViewBudgetSignal,
  overByTimesheetId,
  budgetByKey,
}: ApprovalsTableProps) {
  // Flat rows use `toggleGroup` so a group with a single sub-group opens it
  // straight through (see the hook — the lone tier is otherwise redundant here).
  const { expanded, toggle, toggleGroup, allExpanded, toggleAll } = useExpandableGroups(groups);

  const groupNoun = view === 'employee' ? 'employees' : 'customers';

  return (
    <div className="@container/approvals min-h-0 flex-1 overflow-auto rounded-md border bg-card">
      {truncated ? (
        <div className="flex items-start gap-2 border-b bg-amber-50 px-3 py-2 text-xs text-amber-900 dark:bg-amber-950/40 dark:text-amber-200">
          <TriangleAlert className="mt-0.5 size-3.5 shrink-0" />
          <span>
            Showing {returnedGroups} of {groupTotal} {groupNoun} with pending entries in this range.
            Narrow the filters (customer, employee, or date range) to review the rest — or use
            “Approve all” to clear the entire matching set.
          </span>
        </div>
      ) : null}

      <div
        className={cn(
          'sticky top-0 z-10 grid items-center gap-2 border-b bg-muted px-4 py-3 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground',
          FLAT_GRID,
        )}
      >
        <button
          type="button"
          onClick={toggleAll}
          aria-expanded={allExpanded}
          disabled={groups.length === 0}
          title={allExpanded ? 'Collapse all rows' : 'Expand all rows'}
          className="flex items-center gap-1.5 text-left uppercase tracking-wider text-muted-foreground hover:text-foreground disabled:pointer-events-none disabled:opacity-50"
        >
          <ChevronRightSmall
            className={cn('size-3.5 shrink-0 transition-transform', allExpanded && 'rotate-90')}
          />
          {view === 'employee' ? 'Employee' : 'Customer'}
        </button>
        <div className="text-right">Total</div>
        <div className="text-right">Actions</div>
      </div>

      {groups.map((g) => {
        const isOpen = expanded.has(g.key);
        const overage = canViewBudgetSignal
          ? summarizeOverage(g.entries, overByTimesheetId, budgetByKey)
          : null;
        return (
          <div key={g.key} className="border-b last:border-b-0">
            {/* biome-ignore lint/a11y/useSemanticElements: row contains nested Approve/Reject buttons, so a native <button> would be invalid HTML; role="button" + keyboard handler keeps it accessible */}
            <div
              role="button"
              tabIndex={0}
              aria-expanded={isOpen}
              onClick={() => toggleGroup(g)}
              onKeyDown={(e) => {
                if (e.target !== e.currentTarget) return;
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  toggleGroup(g);
                }
              }}
              className={cn(
                'grid w-full cursor-pointer items-center gap-2 px-3 py-3 text-left hover:bg-muted/30',
                FLAT_GRID,
              )}
            >
              <div className="flex min-w-0 items-center gap-2">
                <ChevronRightSmall
                  className={cn(
                    'size-3.5 shrink-0 text-muted-foreground transition-transform',
                    isOpen && 'rotate-90',
                  )}
                />
                <span
                  className={cn(
                    'inline-flex size-7 shrink-0 items-center justify-center rounded-full text-[11px] font-semibold',
                    view === 'employee'
                      ? 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900/60 dark:text-indigo-200'
                      : 'bg-amber-100 text-amber-800 dark:bg-amber-900/60 dark:text-amber-200',
                  )}
                  aria-hidden
                >
                  {g.initials}
                </span>
                <div className="flex min-w-0 flex-col">
                  <span className="break-words text-sm font-semibold">{g.title}</span>
                  <span className="truncate text-xs text-muted-foreground">
                    {g.subtitle ? `${g.subtitle} · ` : ''}
                    <span className="text-[11px] font-medium">{g.entries.length}</span>{' '}
                    {g.entries.length === 1 ? 'entry' : 'entries'} · {dateSpanLabel(g.entries)}
                  </span>
                  {overage ? (
                    <OverBudgetBadge
                      overHours={overage.totalOverHours}
                      budgetCount={overage.budgetCount}
                      details={overage.details}
                      className="mt-1 w-fit"
                    />
                  ) : null}
                </div>
              </div>
              <div className="text-right text-sm font-semibold tabular-nums">
                {formatHours(g.totalHours)}
              </div>
              <div className="flex items-center justify-end gap-1">
                <GroupApproveButton
                  entries={g.entries}
                  scopeLabel={g.title}
                  busy={busyKey === `approve:g:${g.key}`}
                  loading={decideLoading}
                  onApprove={() => onApproveGroup(g.key)}
                />
                <GroupRejectButton
                  entries={g.entries}
                  scopeLabel={g.title}
                  busy={busyKey === `reject:g:${g.key}`}
                  loading={decideLoading}
                  onConfirm={(reason) => onRejectGroup(g.key, reason)}
                />
              </div>
            </div>

            {isOpen ? (
              <div className="bg-muted/10">
                {g.subGroups.map((sg) => (
                  <SubGroupSection
                    key={sg.key}
                    subGroup={sg}
                    view={view}
                    mode="range"
                    expanded={expanded.has(sg.key)}
                    onToggle={() => toggle(sg.key)}
                    decideLoading={decideLoading}
                    busyKey={busyKey}
                    onApprove={onApproveSubGroup}
                    onReject={onRejectSubGroup}
                    onApproveEntry={onApproveEntry}
                    onRejectEntry={onRejectEntry}
                    canViewBudgetSignal={canViewBudgetSignal}
                    overByTimesheetId={overByTimesheetId}
                    budgetByKey={budgetByKey}
                  />
                ))}
              </div>
            ) : null}
          </div>
        );
      })}
    </div>
  );
}

/** The bulk Approve trigger shared by the week-table group row, the
 *  flat-list group row, and the sub-group row. Mirror of
 *  `GroupRejectButton` on the approve side: a fully-locked scope disables
 *  the button (a locked period can no longer be approved) and moves the
 *  why-disabled copy to a hoverable wrapper. Approve has no reason prompt,
 *  so there's no popover — just the button. */
function GroupApproveButton({
  entries,
  scopeLabel,
  busy,
  loading,
  onApprove,
}: {
  entries: PendingApprovalEntry[];
  scopeLabel: string;
  /** True while THIS scope's approve is in flight (drives the spinner). */
  busy: boolean;
  /** True while any decide is in flight (disables the trigger). */
  loading: boolean;
  onApprove: () => void;
}) {
  const approve = decisionCopy(entries, scopeLabel, 'approve');
  const button = (
    <Button
      size="sm"
      variant="outline"
      disabled={loading || approve.allLocked}
      onClick={(e) => {
        e.stopPropagation();
        onApprove();
      }}
      title={approve.allLocked ? undefined : approve.title}
    >
      <ActionIcon busy={busy} icon={Check} />
      <ActionLabel>Approve</ActionLabel>
    </Button>
  );
  // Fully locked scope: the title on the disabled button itself would never
  // show, so the explanation moves to a hoverable wrapper.
  if (approve.allLocked) {
    return <DisabledActionReason title={approve.title}>{button}</DisabledActionReason>;
  }
  return button;
}

/** The bulk Reject trigger + reason popover shared by the week-table group
 *  row, the flat-list group row, and the sub-group row. Owns `decisionCopy`
 *  so the locked-period disabled state, tooltip, and skip wording can't
 *  drift between the three call sites. */
function GroupRejectButton({
  entries,
  scopeLabel,
  busy,
  loading,
  onConfirm,
}: {
  entries: PendingApprovalEntry[];
  scopeLabel: string;
  /** True while THIS scope's reject is in flight (drives the spinner). */
  busy: boolean;
  /** True while any decide is in flight (disables the trigger). */
  loading: boolean;
  onConfirm: (reason: string) => void;
}) {
  const reject = decisionCopy(entries, scopeLabel, 'reject');
  // `rounded-full` because the popover-trigger path below wraps this in
  // `PopoverTrigger asChild`, which clobbers the Button's own
  // `data-slot="button"` with `data-slot="popover-trigger"` (Radix Slot lets
  // the outer wrapper's explicit props win) — the global `[data-slot='button']`
  // pill-radius rule never matches it there, so the radius has to be set
  // directly. Applied unconditionally (not just on the popover path) so the
  // fully-locked/disabled rendering below stays visually identical.
  const button = (
    <Button
      size="sm"
      variant="outline"
      data-tone="destructive"
      disabled={loading || reject.allLocked}
      onClick={(e) => e.stopPropagation()}
      className="rounded-full text-destructive dark:text-rose-300 dark:hover:bg-rose-900/30"
      title={reject.allLocked ? undefined : reject.title}
    >
      <ActionIcon busy={busy} icon={X} />
      <ActionLabel>Reject</ActionLabel>
    </Button>
  );
  // Fully locked scope: no popover (the reject must not be confirmable),
  // and the why-disabled explanation moves to a hoverable wrapper — a
  // title on the disabled button itself would never show.
  if (reject.allLocked) {
    return <DisabledActionReason title={reject.title}>{button}</DisabledActionReason>;
  }
  return (
    <RejectReasonPopover
      loading={loading}
      description={reject.description}
      onConfirm={onConfirm}
      trigger={button}
    />
  );
}

/** "Jun 12 – Jul 3" span for a group's entries (single date if all equal). */
function dateSpanLabel(entries: PendingApprovalEntry[]): string {
  let min = entries[0]?.date ?? '';
  let max = min;
  for (const e of entries) {
    if (e.date < min) min = e.date;
    if (e.date > max) max = e.date;
  }
  return min === max ? shortDate(min) : `${shortDate(min)} – ${shortDate(max)}`;
}

// ─────────────────────────────────────────────────────────────────────────
// Expanded detail
// ─────────────────────────────────────────────────────────────────────────

interface SubGroupSectionProps {
  subGroup: ApprovalsSubGroup;
  view: ViewMode;
  /** Week renders the 7-day matrix cells; range drops them for a flat row. */
  mode: 'week' | 'range';
  expanded: boolean;
  onToggle: () => void;
  decideLoading: boolean;
  busyKey: string | null;
  onApprove: (key: string) => void;
  onReject: (key: string, reason: string) => void;
  onApproveEntry: (instanceId: string) => void;
  onRejectEntry: (instanceId: string, reason: string) => void;
  canViewBudgetSignal: boolean;
  overByTimesheetId: OverByTimesheetId;
  budgetByKey: ReadonlyMap<string, BudgetSignalRow>;
}

function SubGroupSection({
  subGroup: sg,
  view,
  mode,
  expanded,
  onToggle,
  decideLoading,
  busyKey,
  onApprove,
  onReject,
  onApproveEntry,
  onRejectEntry,
  canViewBudgetSignal,
  overByTimesheetId,
  budgetByKey,
}: SubGroupSectionProps) {
  const subGroupOverage = canViewBudgetSignal
    ? summarizeOverage(sg.entries, overByTimesheetId, budgetByKey)
    : null;
  return (
    <div className="border-t first:border-t-0">
      {/* biome-ignore lint/a11y/useSemanticElements: row contains nested Approve/Reject buttons, so a native <button> would be invalid HTML; role="button" + keyboard handler keeps it accessible */}
      <div
        role="button"
        tabIndex={0}
        aria-expanded={expanded}
        onClick={onToggle}
        onKeyDown={(e) => {
          // Only handle keys aimed at the row itself — see the L1 group row
          // for why: the portalled Reject popover's textarea bubbles its
          // keystrokes through the React tree to here.
          if (e.target !== e.currentTarget) return;
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onToggle();
          }
        }}
        className={cn(
          'grid w-full cursor-pointer items-center gap-2 px-3 py-2 text-left hover:bg-muted/40',
          mode === 'week' ? TABLE_GRID : FLAT_GRID,
        )}
      >
        <div className="flex min-w-0 items-center gap-2 pl-6">
          <ChevronRightSmall
            className={cn(
              'size-3 shrink-0 text-muted-foreground transition-transform',
              expanded && 'rotate-90',
            )}
          />
          <span
            className={cn(
              'inline-flex size-6 shrink-0 items-center justify-center rounded-full text-[10px] font-semibold',
              // The sub-group avatar is colored by what it represents:
              // customer (amber) in the employee view, employee (indigo) in
              // the customer view — the inverse of the L1 row's color.
              view === 'employee'
                ? 'bg-amber-100 text-amber-800 dark:bg-amber-900/60 dark:text-amber-200'
                : 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900/60 dark:text-indigo-200',
            )}
            aria-hidden
          >
            {sg.initials}
          </span>
          <div className="flex min-w-0 flex-col">
            <span className="break-words text-sm font-medium">{sg.title}</span>
            <span className="truncate text-xs text-muted-foreground">
              <span className="text-[11px] font-medium">{sg.entries.length}</span>{' '}
              {sg.entries.length === 1 ? 'entry' : 'entries'}
              {/* Range can span weeks, so append the date span; week mode's
                  fixed 7-day grid makes that redundant. */}
              {mode === 'week' ? '' : ` · ${dateSpanLabel(sg.entries)}`}
            </span>
            {subGroupOverage ? (
              <OverBudgetBadge
                overHours={subGroupOverage.totalOverHours}
                budgetCount={subGroupOverage.budgetCount}
                details={subGroupOverage.details}
                className="mt-1 w-fit"
              />
            ) : null}
          </div>
        </div>
        {/* Week mode fills the 7-day matrix; range mode has no fixed grid. */}
        {mode === 'week'
          ? sg.perDay.map((cell) => {
              const dayOver = canViewBudgetSignal
                ? dayOverHours(sg.entries, cell.date, overByTimesheetId)
                : 0;
              return (
                <div
                  key={cell.date}
                  className={cn(
                    'rounded-md px-1.5 py-1 text-right text-sm tabular-nums',
                    dayOver > 0
                      ? 'bg-red-50 dark:bg-red-950/30'
                      : cell.count > 0
                        ? 'font-medium'
                        : 'text-muted-foreground/50',
                  )}
                >
                  <div>{cell.count > 0 ? formatHours(cell.hours) : '—'}</div>
                  {dayOver > 0 ? (
                    <div className="text-[10px] font-semibold text-red-700 dark:text-red-300">
                      {formatHours(dayOver)} over
                    </div>
                  ) : null}
                </div>
              );
            })
          : null}
        <div className="text-right text-sm tabular-nums">
          <div className="font-semibold">{formatHours(sg.totalHours)}</div>
        </div>
        <div className="flex items-center justify-end gap-1">
          <GroupApproveButton
            entries={sg.entries}
            scopeLabel={sg.title}
            busy={busyKey === `approve:sg:${sg.key}`}
            loading={decideLoading}
            onApprove={() => onApprove(sg.key)}
          />
          <GroupRejectButton
            entries={sg.entries}
            scopeLabel={sg.title}
            busy={busyKey === `reject:sg:${sg.key}`}
            loading={decideLoading}
            onConfirm={(reason) => onReject(sg.key, reason)}
          />
        </div>
      </div>

      {expanded ? (
        <GroupEntries
          entries={sg.entries}
          decideLoading={decideLoading}
          busyKey={busyKey}
          onApproveEntry={onApproveEntry}
          onRejectEntry={onRejectEntry}
          canViewBudgetSignal={canViewBudgetSignal}
          overByTimesheetId={overByTimesheetId}
        />
      ) : null}
    </div>
  );
}

function GroupEntries({
  entries,
  decideLoading,
  busyKey,
  onApproveEntry,
  onRejectEntry,
  canViewBudgetSignal,
  overByTimesheetId,
  // Left indent for the header + rows. Week mode nests under a sub-group (pl-16);
  // range mode expands straight under the group, so it indents less.
  indentClass = 'pl-16',
}: {
  entries: PendingApprovalEntry[];
  decideLoading: boolean;
  busyKey: string | null;
  onApproveEntry: (instanceId: string) => void;
  onRejectEntry: (instanceId: string, reason: string) => void;
  canViewBudgetSignal: boolean;
  overByTimesheetId: OverByTimesheetId;
  indentClass?: string;
}) {
  // Sort by date asc, then by created-time so the rows render in a
  // predictable order even when there are multiple entries on the same day.
  const sorted = useMemo(
    () =>
      [...entries].sort((a, b) =>
        a.date === b.date
          ? a.submittedAt.localeCompare(b.submittedAt)
          : a.date.localeCompare(b.date),
      ),
    [entries],
  );

  // Detail row: per-entry list. The secondary dimension (customer or
  // employee) is already the sub-group header above, so it's dropped here.
  // Each row carries its own Approve/Reject — the group/sub-group headers
  // still offer the bulk fan-out, but reviewers can also act line by line.
  return (
    // `bg-card` (white), not `bg-background` — the latter is the cream PAGE
    // colour, and this section sits inside the same white `bg-card` container
    // as the group/sub-group rows above it. `bg-background` here banded this
    // one section cream against white everywhere else in the same expanded
    // tree — exactly the "not standard" inconsistency this fixes.
    <div className="border-t bg-card">
      <div
        className={cn(
          'grid items-center gap-2 px-4 py-3 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground',
          indentClass,
          DETAIL_GRID,
        )}
      >
        <div>Date</div>
        <div>Service · Role</div>
        <div>Billable</div>
        <div>Description</div>
        <div className="text-right">Hours</div>
        <div className="text-right">Actions</div>
      </div>
      {sorted.map((entry) => {
        // Fill is reserved for the over-budget signal; approval status (not
        // rendered in this queue today — every row here is `submitted`)
        // would own the left stripe, so the two can compose per
        // docs/budgets.md rather than collide.
        const over = canViewBudgetSignal ? overByTimesheetId.get(entry.timesheetId) : undefined;
        // Shared rule — an engagement is shown, its absence never is.
        const engagement = engagementDisplay(entry);
        return (
          <div
            key={entry.approvalInstanceId}
            data-testid="approval-entry-row"
            className={cn(
              'grid items-start gap-2 border-t px-3 py-2 text-sm hover:bg-muted/30',
              over ? 'bg-red-50/60 dark:bg-red-950/10' : undefined,
              indentClass,
              DETAIL_GRID,
            )}
          >
            <div className="text-xs">
              <div className="font-medium">{shortDayName(entry.date)}</div>
              <div className="text-muted-foreground tabular-nums">{shortDate(entry.date)}</div>
            </div>
            <div className="min-w-0 text-xs">
              {entry.productName ? (
                <div className="break-words font-medium">{entry.productName}</div>
              ) : (
                <div className="italic text-muted-foreground">No activity</div>
              )}
              {entry.roleName ? (
                <span className="mt-0.5 inline-block rounded bg-muted px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
                  {entry.roleName}
                </span>
              ) : null}
              {/* Which contract the hours bill under, on EVERY row rather than
                  once per group. The customer above is a rollup that can span
                  several engagements, so the engagement is not a fact about it
                  — it is a fact about this entry, and stating it per row is
                  what keeps a row readable on its own. Rows with nothing
                  attributed show no line; see `engagementDisplay`. */}
              {engagement ? (
                <EngagementLabel
                  className="mt-0.5"
                  label={engagement.label}
                  tone={engagement.tone}
                />
              ) : null}
            </div>
            <BillableBadge billable={entry.billable} />
            <div className="min-w-0 text-xs text-muted-foreground">
              {entry.description ? (
                <span className="line-clamp-2">{entry.description}</span>
              ) : (
                <span className="italic">No description</span>
              )}
            </div>
            <div className="text-right text-sm tabular-nums">
              {over ? (
                <OverBudgetCell overHours={over.overHours}>
                  <div className="font-medium">{formatHours(entry.hours)}</div>
                </OverBudgetCell>
              ) : (
                <div className="font-medium">{formatHours(entry.hours)}</div>
              )}
            </div>
            <div className="flex items-center justify-end gap-1">
              {entry.locked ? (
                <DisabledActionReason title={LOCKED_ENTRY_APPROVE_TOOLTIP}>
                  <Button size="sm" variant="outline" disabled>
                    <ActionIcon busy={false} icon={Check} />
                    <ActionLabel>Approve</ActionLabel>
                  </Button>
                </DisabledActionReason>
              ) : (
                <Button
                  size="sm"
                  variant="outline"
                  disabled={decideLoading}
                  onClick={() => onApproveEntry(entry.approvalInstanceId)}
                  title="Approve this entry"
                >
                  <ActionIcon
                    busy={busyKey === `approve:e:${entry.approvalInstanceId}`}
                    icon={Check}
                  />
                  <ActionLabel>Approve</ActionLabel>
                </Button>
              )}
              {entry.locked ? (
                <DisabledActionReason title={LOCKED_ENTRY_REJECT_TOOLTIP}>
                  <Button
                    size="sm"
                    variant="outline"
                    disabled
                    className="text-rose-700 dark:text-rose-300"
                  >
                    <ActionIcon busy={false} icon={X} />
                    <ActionLabel>Reject</ActionLabel>
                  </Button>
                </DisabledActionReason>
              ) : (
                <RejectReasonPopover
                  loading={decideLoading}
                  description="Reject this entry. The submitter will see this reason on the row."
                  onConfirm={(reason) => onRejectEntry(entry.approvalInstanceId, reason)}
                  trigger={
                    // `PopoverTrigger asChild` clobbers this Button's own
                    // `data-slot="button"` with its own `data-slot="popover-trigger"`
                    // (Radix Slot lets the outer wrapper's explicit props win), so the
                    // global `[data-slot='button']` pill-radius rule never matches this
                    // trigger. Set the radius directly instead of depending on that rule.
                    <Button
                      size="sm"
                      variant="outline"
                      data-tone="destructive"
                      disabled={decideLoading}
                      className="rounded-full text-destructive dark:text-rose-300 dark:hover:bg-rose-900/30"
                      title="Reject this entry"
                    >
                      <ActionIcon
                        busy={busyKey === `reject:e:${entry.approvalInstanceId}`}
                        icon={X}
                      />
                      <ActionLabel>Reject</ActionLabel>
                    </Button>
                  }
                />
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Helpers + small components
// ─────────────────────────────────────────────────────────────────────────

function ViewButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      data-slot="toggle-group-item"
      className={cn(
        'inline-flex h-7 items-center gap-1 rounded-sm px-2 text-xs font-medium transition-colors',
        // Same blue-chip active state as the Timesheets weekly/day toggle —
        // this one had drifted to a plain muted fill with no blue at all.
        active
          ? 'bg-muted text-foreground light:bg-primary light:text-primary-foreground'
          : 'text-muted-foreground hover:bg-muted/60 hover:text-foreground',
      )}
    >
      {children}
    </button>
  );
}

function EmptyState({
  weekLabel,
  hasFilters,
  onClearFilters,
}: {
  weekLabel: string;
  hasFilters: boolean;
  onClearFilters: () => void;
}) {
  return (
    <div className="flex flex-col items-center justify-center rounded-md border border-dashed bg-card/40 p-12 text-center">
      {hasFilters ? (
        <CircleSlash className="mb-3 size-8 text-muted-foreground" />
      ) : (
        <PartyPopper className="mb-3 size-8 text-emerald-500" />
      )}
      <h2 className="text-sm font-semibold">
        {hasFilters
          ? `Nothing to approve for ${weekLabel}`
          : `Congrats! Nothing to approve for ${weekLabel}`}
      </h2>
      <p className="mt-1 max-w-md text-xs text-muted-foreground">
        {hasFilters
          ? 'No pending entries match the current filters. Clear them to see all entries.'
          : "You're all caught up — every submitted entry for this week has been reviewed."}
      </p>
      {hasFilters ? (
        <Button size="sm" variant="ghost" className="mt-3" onClick={onClearFilters}>
          Clear filters
        </Button>
      ) : null}
    </div>
  );
}

function initialsFor(first: string, last: string, fallback: string): string {
  const a = (first || '').trim()[0] ?? '';
  const b = (last || '').trim()[0] ?? '';
  return (a + b).toUpperCase() || (fallback || '?').toUpperCase().slice(0, 2);
}

/**
 * Two-letter avatar initials for a customer (no first/last split). Uses the
 * first letter of the first two words, falling back to the first two letters
 * for single-word names — `initialsFor` would only ever yield one letter here.
 */
function customerInitials(name: string): string {
  const trimmed = name.trim();
  const [a, b] = trimmed.split(/\s+/).filter(Boolean);
  if (a && b) return (a.charAt(0) + b.charAt(0)).toUpperCase();
  return (trimmed.slice(0, 2) || '?').toUpperCase();
}

function round2(n: number): number {
  return Math.round(n * 100) / 100;
}

/** Recompute the footer totals after an optimistic removal so the header
 * count and daily-totals row stay consistent until the refetch lands. */
function pendingTotals(entries: PendingApprovalEntry[]): PendingApprovalsResult['totals'] {
  let hours = 0;
  let amount = 0;
  for (const e of entries) {
    hours += e.hours;
    if (e.amount != null) amount += e.amount;
  }
  return { entries: entries.length, hours: round2(hours), amount: round2(amount) };
}

function stripDefaults(s: Search): Search {
  const out: Search = {};
  if (s.view && s.view !== 'employee') out.view = s.view;
  if (s.period === 'week') {
    out.period = 'week';
    // Week only matters in week mode.
    if (s.week) out.week = s.week;
  } else {
    // Date-range is the default mode, so the 'period' key stays out of the URL.
    // 'all_time' is the range-mode default, so only persist other presets.
    if (s.preset && s.preset !== 'all_time') out.preset = s.preset;
    if (s.preset === 'custom') {
      if (s.from) out.from = s.from;
      if (s.to) out.to = s.to;
    }
  }
  if (s.customerIds?.length) out.customerIds = s.customerIds;
  if (s.userIds?.length) out.userIds = s.userIds;
  return out;
}

/**
 * Shared toast for a bulk decide/approve result — success, partial-failure, or
 * all-failed — used by both the per-row decide path and the range-mode
 * server-side "approve all".
 */
function reportDecideResults(
  results: DecideBulkResultItem[],
  verb: 'Approved' | 'Rejected' | 'Updated',
  // Locked rows the CLIENT filtered out before sending (week-mode approve).
  // Server-side lock vetoes (range approve-all sends the whole queue) are
  // detected from the results below and added to this.
  clientSkippedLocked = 0,
): void {
  const okCount = results.filter((r) => r.ok).length;
  const allFailed = results.filter((r) => !r.ok) as Array<
    Extract<DecideBulkResultItem, { ok: false }>
  >;
  // A locked-period veto isn't a failure — the row simply can't be decided
  // until an admin unlocks the period. Report those as skipped, not failed,
  // so "Approve all" over a queue containing old locked weeks doesn't read
  // as N errors.
  const isLockSkip = (f: { code: string; message: string }) =>
    f.code === 'BUSINESS_RULE_ERROR' && /lock/i.test(f.message);
  const failed = allFailed.filter((f) => !isLockSkip(f));
  const skipped = clientSkippedLocked + allFailed.filter(isLockSkip).length;
  const skipNote =
    skipped > 0
      ? `${skipped} locked ${skipped === 1 ? 'entry was' : 'entries were'} skipped — Please request support to unlock the period first.`
      : undefined;
  const action = verb === 'Approved' ? 'approve' : verb === 'Rejected' ? 'reject' : 'update';

  if (okCount > 0 && failed.length === 0) {
    toast.success(
      `${verb} ${okCount} entr${okCount === 1 ? 'y' : 'ies'}.`,
      skipNote ? { description: skipNote } : undefined,
    );
  } else if (okCount > 0 && failed.length > 0) {
    toast.warning(
      `${verb} ${okCount} entr${okCount === 1 ? 'y' : 'ies'} · ${failed.length} failed`,
      {
        description: [failed[0]?.message ?? 'Some entries could not be updated.', skipNote]
          .filter(Boolean)
          .join(' '),
      },
    );
  } else if (failed.length > 0) {
    toast.error(failed[0]?.message ?? 'Failed to apply decisions.');
  } else if (skipped > 0) {
    toast.info(`Nothing to ${action} — ${skipNote}`);
  } else {
    toast.info('Nothing to approve for the current filters.');
  }
}
